import { isDefined } from "@clipboard-health/util-ts";
import { startOfDay, subDays } from "date-fns";
import { useMemo } from "react";

import {
  type GetShiftsResponse,
  usePaginatedWorkerShifts,
} from "../../WorkerShifts/api/usePaginatedWorkerShifts";

interface UseRecentWorkplaceIdsParams {
  workerId: string;
  lookbackDays: number;
  enabled: boolean;
}

interface UseRecentWorkplaceIdsResult {
  recentWorkplaceWorkedShiftCounts: Map<string, number>;
  isLoading: boolean;
  isError: boolean;
}

const STALE_TIME_MS = 5 * 60 * 1000;
const PAGE_SIZE = 100;

function extractWorkplaceShiftCounts(pages: GetShiftsResponse[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const page of pages) {
    for (const shift of page.data) {
      const workplaceId = shift.relationships?.workplace?.data.id;
      if (isDefined(workplaceId)) {
        counts.set(workplaceId, (counts.get(workplaceId) ?? 0) + 1);
      }
    }
  }

  return counts;
}

export function useRecentWorkplaceWorkedShiftCounts(
  params: UseRecentWorkplaceIdsParams
): UseRecentWorkplaceIdsResult {
  const { workerId, lookbackDays, enabled } = params;

  const shouldFetch = enabled && lookbackDays > 0;

  const { data, isLoading, isError } = usePaginatedWorkerShifts(
    {
      workerId,
      timeline: "PAST",
      filter: { start: { gt: startOfDay(subDays(new Date(), lookbackDays)) } },
      fields: {},
      include: [],
      pageSize: PAGE_SIZE,
      sortBy: "schedule.startAt",
      sortOrder: "desc",
    },
    { enabled: shouldFetch, staleTime: STALE_TIME_MS }
  );

  const recentWorkplaceWorkedShiftCounts = useMemo(() => {
    if (!shouldFetch || !data?.pages) {
      return new Map<string, number>();
    }

    return extractWorkplaceShiftCounts(data.pages);
  }, [shouldFetch, data?.pages]);

  return {
    recentWorkplaceWorkedShiftCounts,
    isLoading: shouldFetch && isLoading,
    isError: shouldFetch && isError,
  };
}
